import { useState } from 'react';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { type LoginInput, loginSchema } from '@news-cms/shared';
import { AlertCircle, KeyRound, Lock, ShieldCheck, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  authErrorMessage,
  sessionQueryOptions,
  useLoginMutation,
  useVerify2faMutation,
} from '@/lib/auth';

export const Route = createFileRoute('/login')({
  beforeLoad: async ({ context }) => {
    // If already authenticated, skip login and go to dashboard.
    const user = await context.queryClient.ensureQueryData(sessionQueryOptions);
    if (user) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/' });
    }
  },
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const login = useLoginMutation();
  const verify = useVerify2faMutation();
  const [challenge, setChallenge] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [useBackup, setUseBackup] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginInput) => {
    try {
      const result = await login.mutateAsync(data);
      if ('requires2fa' in result) {
        setChallenge(result.challenge);
        setCode('');
        return;
      }
      await navigate({ to: '/' });
    } catch {
      // login.error is set — alert below renders the message
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!challenge) return;
    try {
      await verify.mutateAsync({ challenge, code: code.replace(/\s+/g, '') });
      await navigate({ to: '/' });
    } catch {
      // verify.error is set
    }
  };

  const handleBackToCredentials = () => {
    setChallenge(null);
    setCode('');
    setUseBackup(false);
    verify.reset();
  };

  return (
    <div className="flex min-h-full items-center justify-center bg-[#393939] px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center text-center">
          <img
            src="/news1-logo.png"
            alt="News1Live"
            width={56}
            height={56}
            className="mb-3 rounded-xl shadow-lg"
          />
          <h1 className="text-2xl font-bold tracking-tight text-white">News1Live CMS</h1>
          <p className="mt-1 text-sm text-white/60">ระบบจัดการเนื้อหาข่าว</p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-xl dark:border-slate-800 dark:bg-slate-900">
          {!challenge ? (
            <>
              <h2 className="mb-6 text-lg font-semibold text-slate-900 dark:text-slate-50">
                เข้าสู่ระบบ
              </h2>

              {login.isError && (
                <Alert variant="destructive" className="mb-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{authErrorMessage(login.error)}</AlertDescription>
                </Alert>
              )}

              <form
                onSubmit={(e) => {
                  void handleSubmit(onSubmit)(e);
                }}
                className="space-y-4"
                noValidate
              >
                <div>
                  <Label htmlFor="email" className="mb-1.5 block">
                    อีเมล หรือ username
                  </Label>
                  <div className="relative">
                    <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <Input
                      id="email"
                      type="text"
                      autoComplete="username"
                      placeholder="อีเมล หรือ username"
                      className="pl-10"
                      {...register('email')}
                    />
                  </div>
                  {errors.email && (
                    <p className="text-destructive mt-1 text-xs">{errors.email.message}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="password" className="mb-1.5 block">
                    รหัสผ่าน
                  </Label>
                  <div className="relative">
                    <Lock className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <PasswordInput
                      id="password"
                      autoComplete="current-password"
                      placeholder="••••••••"
                      className="pl-10"
                      {...register('password')}
                    />
                  </div>
                  {errors.password && (
                    <p className="text-destructive mt-1 text-xs">{errors.password.message}</p>
                  )}
                </div>

                <Button type="submit" size="lg" disabled={login.isPending} className="w-full">
                  {login.isPending ? 'กำลังเข้าสู่ระบบ...' : 'เข้าสู่ระบบ'}
                </Button>
              </form>
            </>
          ) : (
            <>
              <h2 className="mb-1 flex items-center gap-2 text-lg font-semibold text-slate-900 dark:text-slate-50">
                <ShieldCheck className="text-brand-600 h-5 w-5" />
                ยืนยันตัวตน 2 ขั้นตอน
              </h2>
              <p className="mb-6 text-sm text-slate-500 dark:text-slate-400">
                {useBackup
                  ? 'ใส่ backup code ที่บันทึกไว้ตอนตั้ง 2FA'
                  : 'ใส่รหัส 6 หลักจากแอป Authenticator'}
              </p>

              {verify.isError && (
                <Alert variant="destructive" className="mb-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{authErrorMessage(verify.error)}</AlertDescription>
                </Alert>
              )}

              <form
                onSubmit={(e) => {
                  void handleVerify(e);
                }}
                className="space-y-4"
              >
                <div>
                  <Label htmlFor="otp" className="mb-1.5 block">
                    {useBackup ? 'Backup code' : 'รหัส 6 หลัก'}
                  </Label>
                  <div className="relative">
                    {useBackup ? (
                      <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    ) : (
                      <ShieldCheck className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    )}
                    <Input
                      id="otp"
                      autoComplete="one-time-code"
                      inputMode={useBackup ? 'text' : 'numeric'}
                      placeholder={useBackup ? 'XXXXX-XXXXX' : '123456'}
                      className="pl-10 font-mono tracking-widest"
                      value={code}
                      onChange={(e) => {
                        const v = useBackup
                          ? e.target.value
                          : e.target.value.replace(/\D/g, '').slice(0, 6);
                        setCode(v);
                      }}
                      autoFocus
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  size="lg"
                  disabled={!code || verify.isPending}
                  className="w-full"
                >
                  {verify.isPending ? 'กำลังยืนยัน...' : 'ยืนยัน'}
                </Button>

                <div className="flex items-center justify-between text-xs">
                  <button
                    type="button"
                    onClick={() => {
                      setUseBackup((v) => !v);
                      setCode('');
                      verify.reset();
                    }}
                    className="text-brand-600 hover:underline"
                  >
                    {useBackup ? 'ใช้รหัสจากแอปแทน' : 'ใช้ backup code แทน'}
                  </button>
                  <button
                    type="button"
                    onClick={handleBackToCredentials}
                    className="text-slate-500 hover:text-slate-700 hover:underline dark:hover:text-slate-300"
                  >
                    เริ่มใหม่
                  </button>
                </div>
              </form>
            </>
          )}

          <p className="mt-6 text-center text-xs text-slate-500 dark:text-slate-400">
            v0.2.0 · Pure Cloudflare stack
          </p>
        </div>
      </div>
    </div>
  );
}
